import {Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from "@angular/core";
import {DICTIONARY} from "../../dictionary";
import {SERVERCONF} from "../../constants"
import {GeneralService} from "../../services/generalService";
import {RouteService} from "../../services/routeService";
import {NotificationService} from "../../services/notificationService";
import {loadStripe} from '@stripe/stripe-js/pure';


@Component({
	selector: 'stripe-buttons-component',
	template: `<div id="stripe-button-container"></div>`
})
export class StripeButtonsComponent implements OnInit, OnDestroy, OnChanges {

	@Input() type;
	@Input() partner;
	@Input() numUsers;
	@Input() accountDetails;
	@Input() hasAccessToken;
	@Input() couponCode;
	@Input() priceId;
	@Output() onComplete = new EventEmitter<any>;

	constructor(private gs:GeneralService,
				private rs:RouteService,
				private ns:NotificationService) {
	}

	dic = DICTIONARY;
	paypalMode;
	stripeClientId;
	orderId;
	payerId;
	checkout;
	stripe;
	// CC: incremented on every input change/destroy so debounced and in-flight checkout creations can detect they are stale
	changeSeq = 0;

	async fetchClientSecret() {
		if ((this.type === this.dic.CONSTANTS.paypalPaymentType.newUser || this.type === this.dic.CONSTANTS.paypalPaymentType.upgradePro)
			&& (this.numUsers < 1 || this.numUsers > 10)) {
			this.ns.showErrorMessage('Number of users should be between 1-10');
			return;
		}
		// CC: the license count input is live in both the "adjust licenses" and "renewal" popups - block empty/zero/negative values
		if ((this.type === this.dic.CONSTANTS.paypalPaymentType.extendPlanLicenses || this.type === this.dic.CONSTANTS.paypalPaymentType.extendPlanExpiration)
			&& (!this.numUsers || this.numUsers < 1)) {
			this.ns.showErrorMessage('Number of users must be bigger than 1');
			return;
		}

		if (!this.gs.validateEmail(this.accountDetails.email)) {
			this.ns.showErrorMessage('Email is invalid');
			return;
		}

		return this.rs[this.hasAccessToken ? 'stripeCreateOrderI':'stripeCreateOrder']({
			type: this.type,
			sandbox: this.partner.paypalSandbox,
			customerId: this.partner.id,
			priceId: this.priceId || '',
			coupon: this.couponCode || '',
			email: this.accountDetails.email,
			firstName: this.accountDetails.firstName,
			lastName: this.accountDetails.lastName,
			phone: this.accountDetails.phone,
			numUsers: this.numUsers || 0,
		}).then(res => {
			console.log('Stripe: create', [res.orderId, res.payerId]);

			this.orderId = res.orderId;
			this.payerId = res.payerId;
			return res.client_secret;
		}, err => {
			console.error('Stripe: create', err);
			this.onComplete.emit({err});
		});
	}

	async handleComplete() {
		return this.rs[this.hasAccessToken ? 'stripeExecuteOrderI':'stripeExecuteOrder']({
			type: this.type,
			sandbox: this.partner.paypalSandbox,
			accountDetails: this.accountDetails,
			customerId: this.partner.id,
			orderId: this.orderId,
			payerId: this.payerId,
			numUsers: this.numUsers,
		}).then(res => {
			console.log('Stripe: execute', this.orderId);
			this.checkout.destroy();
			this.checkout = null;

			this.onComplete.emit({res});
		}, err => {
			console.error('Stripe: execute', err);
			this.checkout.destroy();
			this.checkout = null;

			this.onComplete.emit({err});
		});
	}

	ngOnChanges(changes: SimpleChanges) {
		if (!this.stripe) {
			return;
		}

		console.log('Values changed: ' + Object.keys(changes));
		// CC: debounce keystrokes so a checkout order is not created per intermediate value, and drop stale recreations that were superseded by a newer change
		const seq = ++this.changeSeq;
		setTimeout(() => {
			if (seq !== this.changeSeq) {
				return;
			}
			if (this.checkout) {
				this.checkout.destroy();
				this.checkout = null;
			}
			this.stripe.initEmbeddedCheckout({
				fetchClientSecret: this.fetchClientSecret.bind(this),
				onComplete: this.handleComplete.bind(this)
			}).then(checkout => {
				// CC: a newer change (or destroy) happened while this checkout was being created - discard it instead of mounting a stale order
				if (seq !== this.changeSeq) {
					checkout.destroy();
					return;
				}
				this.checkout = checkout;
				this.checkout.mount('#stripe-button-container');
			}, err => {
				console.error('Recreate stripe error: ', err);
			});
		}, 500);
	}

	ngOnInit() {
		this.paypalMode = this.partner && this.partner.paypalSandbox && 'sandbox' || SERVERCONF.paypalMode;
		this.stripeClientId = this.paypalMode === 'sandbox' ? SERVERCONF.stripeSandboxClientId : SERVERCONF.stripeLiveClientId;

		if (!this.partner || !this.stripeClientId || this.checkout) return;

		if (this.type === this.dic.CONSTANTS.paypalPaymentType.extendPlanExpiration || this.type === this.dic.CONSTANTS.paypalPaymentType.extendPlanLicenses) {
			try {loadStripe.setLoadParameters({advancedFraudSignals: false})} catch(ex) {}
		}
		loadStripe(this.stripeClientId).then(stripe => {
			this.stripe = stripe;

			const seq = this.changeSeq;
			stripe.initEmbeddedCheckout({
				fetchClientSecret: this.fetchClientSecret.bind(this),
				onComplete: this.handleComplete.bind(this),
				//onShippingDetailsChange(evt)
				//onLineItemsChange(evt)
			}).then(checkout => {
				// CC: an input change (or destroy) happened while the initial checkout was being created - discard it, the change handler recreates it
				if (seq !== this.changeSeq) {
					checkout.destroy();
					return;
				}
				this.checkout = checkout;
				this.checkout.mount('#stripe-button-container');
			}, err => {
				console.error('Init stripe error: ', err);
			});
		}, err => {
			console.error('Load stripe error: ', err);
		});
	}

	ngOnDestroy() {
		// CC: invalidate any debounced/in-flight checkout creation so it won't mount after the component is gone
		this.changeSeq++;
		if (this.checkout) {
			this.checkout.destroy();
		}
	}
}
